import { ChevronDownIcon, XIcon } from '@page/icons';
import { forwardRef, memo, useCallback, useState } from 'react';

import { cn } from '../../lib/styles';
import { Button, ButtonProps, ButtonSize } from '../Button';
import { Tooltip } from '../Tooltip';

interface SelectTriggerClearIconProps {
  onClear: () => void;
}

/** Swaps the caret for an X on hover; click clears without opening the menu. */
const SelectTriggerClearIcon = ({ onClear }: SelectTriggerClearIconProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLSpanElement>) => {
      if (!isHovered) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    },
    [isHovered],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      if (!isHovered) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onClear();
    },
    [isHovered, onClear],
  );

  return (
    <Tooltip label="Clear selection" asChild withinPortal open={isHovered}>
      <span
        className="inline-flex shrink-0"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
      >
        {isHovered ? <XIcon className="size-5 shrink-0" /> : <ChevronDownIcon className="size-5 min-w-5" />}
      </span>
    </Tooltip>
  );
};

export interface SelectTriggerProps {
  size?: ButtonSize;
  variant?: ButtonProps['variant'];
  disabled?: boolean;
  error?: string;
  className?: string;
  placeholderClassName?: string;
  isPlaceholder?: boolean;
  suffix?: React.ReactNode;
  clearable?: boolean;
  onClear?: () => void;
  'aria-label'?: string;
  children: React.ReactNode;
}

const SelectTriggerBase = (
  {
    size,
    variant = 'secondary',
    disabled,
    error,
    className,
    placeholderClassName,
    isPlaceholder,
    suffix,
    clearable = false,
    onClear,
    children,
    ...rest
  }: SelectTriggerProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  const showClearTrigger = clearable && !isPlaceholder && onClear;

  return (
    <Button
      ref={ref}
      type="button"
      variant={variant}
      rightIcon={
        showClearTrigger ? <SelectTriggerClearIcon onClear={onClear} /> : <ChevronDownIcon className="size-5 min-w-5" />
      }
      disabled={disabled}
      size={size}
      className={cn(
        'max-w-xl truncate **:data-[slot="content"]:justify-between fill-icon-primary px-2 font-medium',
        { 'border-text-destructive hover:border-text-destructive': !!error },
        className,
      )}
      {...rest}
    >
      <div
        className={cn(
          'truncate',
          {
            'text-text-tertiary font-normal': isPlaceholder,
          },
          isPlaceholder && placeholderClassName,
        )}
      >
        {children}
      </div>
      {suffix}
    </Button>
  );
};

export const SelectTrigger = memo(forwardRef(SelectTriggerBase));

SelectTrigger.displayName = 'SelectTrigger';
